import { StorageObjectEventRecordBase, type StorageObjectGroupStateRecord } from '@navio/nsdk';
import { cn, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { NoddiBadge } from 'noddi-ui-web';
import {
  getStorageGroupLocationDisplay,
  getStorageGroupStatus,
  NoddiDateFormats,
  noddiFormatDate,
  type StorageGroupStatusKind
} from 'noddi-util';

type StorageUnitLike = {
  name: string;
  storageAreaName?: string;
  storageArea?: { name: string } | null;
  storageFacility?: { name: string } | null;
};

interface Props {
  state?: StorageObjectGroupStateRecord | null;
  latestEvent?: StorageObjectEventRecordBase | null;
  /** Destination unit when showing a pickup target (facility + area • unit). */
  storageUnit?: StorageUnitLike | null;
}

const statusIconByKind: Record<StorageGroupStatusKind, NoddiIconName> = {
  noHistory: 'Package',
  lost: 'Package',
  discarded: 'Package',
  withCustomer: 'Home',
  notReceived: 'Home',
  pickedUpFromCustomer: 'PackageCheck',
  pickedUpFromSupplier: 'PackagePlus',
  processing: 'Package',
  inTransit: 'Truck',
  inTransitUnknownOrigin: 'CircleHelp',
  inStorage: 'Warehouse',
  atFacility: 'Building2'
};

export const StorageLocationInfo = ({ state, latestEvent, storageUnit: storageUnitProp }: Props) => {
  // Events are never updated in place; who recorded it and when live on actorName/createdAt.
  const metaFooter = latestEvent ? (
    <div className='flex items-center gap-2 border-t border-muted-foreground pt-1 text-xs'>
      <NoddiIcon name='User' size='xSmall' />
      <span className='truncate'>{latestEvent.actorName || 'Unknown'}</span>
      <span className='text-nowrap'>• {noddiFormatDate(latestEvent.createdAt, NoddiDateFormats.DATE_TIME)}</span>
    </div>
  ) : null;

  const locationFromState = getStorageGroupLocationDisplay(state);

  if (locationFromState) {
    return (
      <div className='flex flex-col gap-1.5'>
        {locationFromState.facilityName && (
          <div className='flex items-center gap-2'>
            <NoddiIcon name='Building2' size='xSmall' />
            <span className='text-sm text-nowrap'>{locationFromState.facilityName}</span>
          </div>
        )}

        {locationFromState.unitLine && (
          <div className='flex flex-col gap-1 text-xs'>
            <div className='flex items-center gap-2'>
              <NoddiIcon name='MapPin' size='xSmall' />
              <span className='text-nowrap'>{locationFromState.unitLine}</span>
            </div>
          </div>
        )}

        {locationFromState.showOrigin && (locationFromState.originFacilityName || locationFromState.originUnitLine) && (
          <div className={cn('flex flex-col gap-1 text-xs opacity-60')}>
            <NoddiBadge variant='outline' className='h-4 w-fit px-1 text-[10px]'>
              Origin
            </NoddiBadge>
            {locationFromState.originFacilityName && (
              <div className='flex items-center gap-2'>
                <NoddiIcon name='Building2' size='xSmall' />
                <span className='text-sm text-nowrap'>{locationFromState.originFacilityName}</span>
              </div>
            )}
            {locationFromState.originUnitLine && (
              <div className='flex items-center gap-2'>
                <NoddiIcon name='MapPin' size='xSmall' />
                <span className='text-nowrap'>{locationFromState.originUnitLine}</span>
              </div>
            )}
          </div>
        )}

        {metaFooter}
      </div>
    );
  }

  const unit = storageUnitProp;
  if (unit) {
    const facilityName = unit.storageFacility?.name;
    const areaName = unit.storageArea?.name ?? unit.storageAreaName;

    return (
      <div className='flex flex-col gap-1.5'>
        {facilityName && (
          <div className='flex items-center gap-2'>
            <NoddiIcon name='Building2' size='xSmall' />
            <span className='text-sm text-nowrap'>{facilityName}</span>
          </div>
        )}
        <div className='flex flex-col gap-1 text-xs'>
          <div className='flex items-center gap-2'>
            <NoddiIcon name='MapPin' size='xSmall' />
            <span className='text-nowrap'>{areaName ? `${areaName} • ${unit.name}` : unit.name}</span>
          </div>
        </div>
      </div>
    );
  }

  const status = getStorageGroupStatus(state);

  return (
    <div className='flex flex-col gap-1.5'>
      <div className='flex items-center gap-2'>
        <NoddiIcon name={statusIconByKind[status.kind]} size='xSmall' />
        <span className='text-sm text-nowrap'>{status.label}</span>
      </div>
      {metaFooter}
    </div>
  );
};
